/**
 * Message Service
 *
 * Internes Nachrichtenmodul (1:1 + Admin-Gruppen). Gleiches fetch()-Muster
 * wie absence.service.ts - kein genereller apiClient.
 */
import { useAuthStore } from '@/modules/auth/stores/auth.store'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1'
const TENANT_SLUG = import.meta.env.VITE_TENANT_SLUG || 'studio-luebeck'
const BASE = `${API_BASE_URL}/tenants/${TENANT_SLUG}/messages`

export interface Contact {
  userId: string
  name: string
  role: 'admin' | 'teacher' | 'student' | 'guardian'
  /** Teacher-/Student-Stammdaten-ID (nicht die User-ID) - für den "Termin
   * teilen"-Picker (lesson-slots?teacherId=&studentId=). */
  teacherId: string | null
  studentId: string | null
}

export interface ConversationParticipantSummary {
  userId: string
  name: string
  role: 'admin' | 'teacher' | 'student' | 'guardian'
  teacherId: string | null
  studentId: string | null
}

export interface ConversationSummary {
  id: string
  isGroup: boolean
  title: string
  participants: ConversationParticipantSummary[]
  lastMessage: { body: string; createdAt: string; senderId: string } | null
  lastMessageAt: string
  unreadCount: number
}

/** 'text' | 'file' | 'appointment_share' | 'appointment_proposal' */
export type MessageType = 'text' | 'file' | 'appointment_share' | 'appointment_proposal'
/** Nur bei 'appointment_proposal' gesetzt. */
export type AppointmentStatus = 'pending' | 'accepted' | 'declined'

export interface ApiMessage {
  id: string
  body: string
  createdAt: string
  senderId: string
  senderName?: string
  type: MessageType
  attachmentFileName: string | null
  attachmentOriginalName: string | null
  attachmentMimeType: string | null
  attachmentSize: number | null
  appointmentDate: string | null
  appointmentDayOfWeek: number | null
  appointmentStartTime: string | null
  appointmentEndTime: string | null
  appointmentSubject: string | null
  appointmentStatus: AppointmentStatus | null
  appointmentRespondedAt: string | null
  appointmentRespondedBy: string | null
}

/** Basis-URL der ausgelieferten Anhänge - gleicher statischer Mount wie
 * Lehrer-Fotos/Qualifikationsnachweise. */
export const MESSAGE_ATTACHMENT_BASE_URL = `${API_BASE_URL}/uploads/message-attachments/`

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const authStore = useAuthStore()
  // FormData (Datei-Upload) braucht den Content-Type NICHT gesetzt - der
  // Browser setzt ihn selbst inkl. Multipart-Boundary (gleiches Muster wie
  // teacher.service.ts).
  const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData
  const response = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}),
      ...(options?.headers || {})
    }
  })
  if (!response.ok) {
    let detail = ''
    try {
      const body = await response.json()
      detail = body?.error ? `: ${body.error}` : ''
    } catch {
      // Antwort war kein JSON - ignorieren
    }
    throw new Error(`Nachrichten-API-Fehler (${response.status})${detail}`)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const messageService = {
  async contacts(): Promise<{ items: Contact[]; total: number }> {
    return request('/contacts')
  },
  async conversations(): Promise<{ items: ConversationSummary[]; total: number }> {
    return request('/conversations')
  },
  async unreadCount(): Promise<number> {
    const res = await request<{ unreadCount: number }>('/unread-count')
    return res.unreadCount
  },
  async startConversation(recipientUserId: string, body: string): Promise<{ conversationId: string }> {
    return request('/conversations', { method: 'POST', body: JSON.stringify({ recipientUserId, body }) })
  },
  async messages(conversationId: string): Promise<{ items: ApiMessage[]; total: number }> {
    return request(`/conversations/${conversationId}/messages`)
  },
  async sendMessage(conversationId: string, body: string): Promise<ApiMessage> {
    return request(`/conversations/${conversationId}/messages`, { method: 'POST', body: JSON.stringify({ body }) })
  },
  async markRead(conversationId: string): Promise<void> {
    return request(`/conversations/${conversationId}/read`, { method: 'POST' })
  },
  // Gruppen - admin-only (Backend lehnt andere Rollen mit 403 ab).
  async createGroup(data: {
    name: string
    filterType: 'manual' | 'smart'
    memberUserIds?: string[]
    teacherId?: string
    dayOfWeek?: number
    subject?: string
    body?: string
  }): Promise<{ conversationId: string; memberCount: number }> {
    return request('/conversations/group', { method: 'POST', body: JSON.stringify(data) })
  },
  async syncGroupMembers(conversationId: string): Promise<{ items: ConversationParticipantSummary[] }> {
    return request(`/conversations/${conversationId}/sync-members`, { method: 'POST' })
  },
  async addParticipant(conversationId: string, userId: string): Promise<void> {
    return request(`/conversations/${conversationId}/participants`, { method: 'POST', body: JSON.stringify({ userId }) })
  },
  async removeParticipant(conversationId: string, userId: string): Promise<void> {
    return request(`/conversations/${conversationId}/participants/${userId}`, { method: 'DELETE' })
  },
  async uploadAttachment(conversationId: string, file: File, caption?: string): Promise<ApiMessage> {
    const formData = new FormData()
    formData.append('file', file)
    if (caption) formData.append('caption', caption)
    return request(`/conversations/${conversationId}/attachments`, { method: 'POST', body: formData })
  },
  async shareAppointment(conversationId: string, lessonSlotId: string): Promise<ApiMessage> {
    return request(`/conversations/${conversationId}/appointment-share`, { method: 'POST', body: JSON.stringify({ lessonSlotId }) })
  },
  async proposeAppointment(
    conversationId: string,
    data: { date: string; startTime: string; endTime: string; subject: string }
  ): Promise<ApiMessage> {
    return request(`/conversations/${conversationId}/appointment-proposal`, { method: 'POST', body: JSON.stringify(data) })
  },
  async respondToProposal(conversationId: string, messageId: string, status: 'accepted' | 'declined'): Promise<ApiMessage> {
    return request(`/conversations/${conversationId}/messages/${messageId}/respond`, { method: 'POST', body: JSON.stringify({ status }) })
  }
}
